import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Header } from 'components/header/header.component';
import { delay, filter, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageQueryService } from '@certemy/ui-components';
import { STEP_COLOR_STATUSES } from 'constants/status.constants';
import { Subject } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import _differenceBy from 'lodash-es/differenceBy';
import _get from 'lodash-es/get';
import { ResizeService } from 'services/resize/resize.service';
import { NotificationsService } from 'services/notifications/notifications.service';
import { EducationStepNotification } from 'pages/professional/professional-step-notifications.resolver';
import { Selectors as RouterSelectors } from 'features/router-state/+state/router-state.state';
import { FEATURES, FeatureToggleService } from 'features/feature-toggle';
import { ProfilesState, Selectors as ProfileSelectors } from 'features/profiles-state/+state/profiles-state.state';
import { WORKFLOW_MODULE_ID } from 'constants/workflow.constants';
import { UserProfile } from 'services/user/user.interface';
import { getMenuTitles } from 'constants/certification.constants';

@UntilDestroy()
@Component({
  selector: 'cm-professional',
  styleUrls: ['./professional.component.scss'],
  templateUrl: './professional.component.html',
})
export class ProfessionalComponent implements OnInit, OnDestroy {
  STEP_COLOR_STATUSES = STEP_COLOR_STATUSES;
  STEP_NOTIFICATIONS_QUANTITY = {
    DESKTOP: 3,
    MOBILE: 1,
    TO_SHOW: 0,
  };
  messages: EducationStepNotification[] = [];
  @ViewChild('notificationMessage') notificationMessage: TemplateRef<any>;
  private addMessage$ = new Subject<any>();
  menuSettings: Header.Settings = {
    logoLink: '/professional',
    mainMenu: [],
  };

  canDeactivate(): boolean {
    this.messageService.eraseQuery();
    return true;
  }

  constructor(
    private store: Store<any>,
    private router: Router,
    private route: ActivatedRoute,
    private messageService: MessageQueryService,
    private notificationsService: NotificationsService,
    private resizeService: ResizeService,
    private featureToggleService: FeatureToggleService,
  ) {}

  ngOnInit() {
    this.store
      .select(ProfileSelectors.profilesState)
      .pipe(take(1))
      .subscribe((profilesState: ProfilesState) => {
        const { currentProfile } = profilesState;
        const { origin } = currentProfile;
        if (!origin) {
          this.showNoOriginMessages();
        }
        this.initMainMenu(currentProfile);
      });

    this.featureToggleService
      .hasFeatures(FEATURES.TRANSCRIPT)
      .pipe(
        take(1),
        filter((hasFeature) => hasFeature),
      )
      .subscribe(() => {
        this.menuSettings.mainMenu.push({
          link: '/professional/transcripts',
          label: 'Transcripts',
        });
      });

    this.menuSettings.mainMenu.push({
      externalLink: 'https://certemy.com/user-guide-professional/',
      label: 'User Guide',
    });

    this.resizeService.resize$.pipe(untilDestroyed(this)).subscribe(({ isDesktop }) => {
      this.STEP_NOTIFICATIONS_QUANTITY.TO_SHOW = isDesktop
        ? this.STEP_NOTIFICATIONS_QUANTITY.DESKTOP
        : this.STEP_NOTIFICATIONS_QUANTITY.MOBILE;
    });

    this.addMessage$
      .pipe(delay(1000), untilDestroyed(this))
      .subscribe((message) => this.messageService.addCustomMessage(message, this.notificationMessage));
  }

  ngOnDestroy() {}

  private initMainMenu(currentProfile: UserProfile) {
    const { credentialTitle, testingTitle } = getMenuTitles(currentProfile);

    const { testingWorkflow, sharedTestingWorkflow } = _get(currentProfile, 'modules');
    const showTestingModule = testingWorkflow || sharedTestingWorkflow;

    this.menuSettings.mainMenu.push(
      {
        label: credentialTitle,
        link: '/professional/certifications',
      },
      ...(showTestingModule
        ? [
            {
              label: testingTitle,
              link: `/professional/workflows/${WORKFLOW_MODULE_ID.TESTING_WORKFLOW}/workflow`,
            },
          ]
        : []),
      {
        link: '/professional/digital-wallet',
        label: 'Digital Wallet',
      },
    );
  }

  private showNoOriginMessages() {
    const messages = this.route.snapshot.data.messages as EducationStepNotification[];
    this.messages = _differenceBy(messages, this.notificationsService.viewedNotificationsIds, 'id');

    const messagesToShow = this.messages.splice(0, this.STEP_NOTIFICATIONS_QUANTITY.TO_SHOW);
    messagesToShow.forEach((message) => this.addMessage$.next(message));
  }

  removeNotificationMessage(message: { id: number; messageId: number }) {
    this.messageService.removeMessage(message);

    if (!!this.messages.length) {
      this.addMessage$.next(this.messages.shift());
    }
    this.notificationsService.addToViewedNotifications(message.messageId);
  }

  onSeeOpportunities(event, stepId: number) {
    this.store
      .select(RouterSelectors.getActivatedRouterParams)
      .pipe(take(1))
      .subscribe((params) => {
        if (!params.step_id || +params.step_id !== stepId) {
          this.router.navigateByUrl(`/professional/certifications/steps/${stepId}`);
        }
        this.eraseMessages(stepId);
      });
  }

  private eraseMessages(id: number) {
    this.notificationsService.addToViewedNotifications(id);
    this.messageService.eraseQuery();
  }
}
